import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marketsAPI } from '../../services/api';
import '../../components/Layout/Layout.css';

const MARKET_CONFIG = {
  india:  { label: 'Indian Market',  flag: '🇮🇳', currency: '₹', color: '#00ff88', exchange: 'NSE/BSE',      hours: '9:15 AM - 3:30 PM IST' },
  us:     { label: 'US Market',      flag: '🇺🇸', currency: '$', color: '#00b8ff', exchange: 'NYSE/NASDAQ',  hours: '9:30 AM - 4:00 PM EST' },
  crypto: { label: 'Crypto Market',  flag: '₿',   currency: '$', color: '#ffb800', exchange: 'Global',       hours: '24/7' },
  global: { label: 'Global Markets', flag: '🌍',  currency: '$', color: '#a855f7', exchange: 'Multi-Exchange',hours: 'Various' },
};

export default function MarketPage() {
  const { market = 'india' } = useParams();
  const config = MARKET_CONFIG[market] || MARKET_CONFIG.india;

  const [indices,  setIndices]  = useState([]);
  const [quotes,   setQuotes]   = useState([]);
  const [news,     setNews]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [sortBy,   setSortBy]   = useState('market_cap');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [market]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [idx, q, n] = await Promise.all([
        marketsAPI.getIndices(market),
        marketsAPI.getLiveQuotes(market),
        marketsAPI.getNews(market),
      ]);
      setIndices(idx || []);
      setQuotes(q?.items || q || []);
      setNews(n    || []);
      setLastUpdate(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fmt = (val, currency = config.currency) => {
    if (!val) return '--';
    const num = parseFloat(val);
    if (num >= 1e12) return `${currency}${(num/1e12).toFixed(2)}T`;
    if (num >= 1e9)  return `${currency}${(num/1e9).toFixed(2)}B`;
    if (num >= 1e6)  return `${currency}${(num/1e6).toFixed(2)}M`;
    return `${currency}${num.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const fmtPrice = (val) => {
    if (!val) return '--';
    return `${config.currency}${parseFloat(val).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const filtered = quotes.filter(q =>
    !search || q.symbol?.toLowerCase().includes(search.toLowerCase()) || q.name?.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (sortBy === 'change_percent') return (b.change_percent || 0) - (a.change_percent || 0);
    if (sortBy === 'price')          return (b.price || 0)          - (a.price || 0);
    return (b.market_cap || 0) - (a.market_cap || 0);
  });

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="page-title">{config.flag} {config.label}</div>
          <div className="page-subtitle">
            {config.exchange} &nbsp;·&nbsp; {config.hours}
            {lastUpdate && <span> &nbsp;·&nbsp; Updated {lastUpdate.toLocaleTimeString()}</span>}
          </div>
        </div>
        <button className="btn btn-outline" onClick={fetchData} disabled={loading}>
          {loading ? '⟳ Loading...' : '⟳ Refresh'}
        </button>
      </div>

      {/* Market Switcher */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {Object.entries(MARKET_CONFIG).map(([key, cfg]) => (
          <Link key={key} to={`/markets/${key}`}
            style={{
              padding: '8px 18px', borderRadius: '20px', textDecoration: 'none',
              fontSize: '13px', fontWeight: '600', transition: 'all 0.2s',
              background: market === key ? cfg.color : 'rgba(255,255,255,0.05)',
              color: market === key ? '#080c14' : 'rgba(255,255,255,0.5)',
              border: `1px solid ${market === key ? cfg.color : 'rgba(255,255,255,0.1)'}`,
            }}>
            {cfg.flag} {cfg.label}
          </Link>
        ))}
      </div>

      {/* Indices */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        {loading && indices.length === 0
          ? [1,2,3].map(i => (
              <div key={i} className="stat-card" style={{ opacity: 0.5 }}>
                <div className="stat-label">Loading...</div>
                <div className="stat-value" style={{ color: config.color }}>--</div>
              </div>
            ))
          : indices.map((idx, i) => (
              <div key={i} className="stat-card">
                <div className="stat-label">{idx.name}</div>
                <div className="stat-value" style={{ fontSize: '22px', color: idx.change_percent >= 0 ? config.color : '#ff4d4d' }}>
                  {fmtPrice(idx.price)}
                </div>
                <div className="stat-change" style={{ color: idx.change_percent >= 0 ? '#00ff88' : '#ff4d4d' }}>
                  {idx.change_percent >= 0 ? '▲' : '▼'} {Math.abs(idx.change_percent || 0).toFixed(2)}%
                  &nbsp;({idx.change >= 0 ? '+' : ''}{(idx.change || 0).toFixed(2)})
                </div>
              </div>
            ))
        }
      </div>

      {/* Stocks + News */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px' }}>

        {/* Stocks Table */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div className="card-title" style={{ margin: 0 }}>Live Prices</div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search symbol or name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 14px', color: '#fff', fontSize: '13px', outline: 'none', width: '200px' }}
              />
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                style={{ background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '13px' }}>
                <option value="market_cap">Market Cap</option>
                <option value="change_percent">% Change</option>
                <option value="price">Price</option>
              </select>
            </div>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Price</th>
                  <th>Change</th>
                  <th>% Change</th>
                  <th>Day High</th>
                  <th>Day Low</th>
                  <th>Market Cap</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0
                  ? Array(5).fill(0).map((_, i) => (
                      <tr key={i}>
                        {Array(8).fill(0).map((_, j) => (
                          <td key={j}><div style={{ height: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}></div></td>
                        ))}
                      </tr>
                    ))
                  : filtered.map((stock, i) => (
                      <tr key={i}>
                        <td>
                          <div style={{ fontWeight: '700', color: '#fff' }}>{stock.symbol}</div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{stock.name}</div>
                          {stock.sector && <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.2)' }}>{stock.sector}</div>}
                        </td>
                        <td style={{ fontWeight: '600' }}>{fmtPrice(stock.price)}</td>
                        <td style={{ color: stock.change >= 0 ? '#00ff88' : '#ff4d4d' }}>
                          {stock.change >= 0 ? '+' : ''}{(stock.change || 0).toFixed(2)}
                        </td>
                        <td>
                          <span style={{
                            padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                            background: stock.change_percent >= 0 ? 'rgba(0,255,136,0.1)' : 'rgba(255,77,77,0.1)',
                            color: stock.change_percent >= 0 ? '#00ff88' : '#ff4d4d',
                          }}>
                            {stock.change_percent >= 0 ? '▲' : '▼'} {Math.abs(stock.change_percent || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td style={{ color: 'rgba(255,255,255,0.6)' }}>{fmtPrice(stock.day_high)}</td>
                        <td style={{ color: 'rgba(255,255,255,0.6)' }}>{fmtPrice(stock.day_low)}</td>
                        <td style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>{fmt(stock.market_cap)}</td>
                        <td>
                          <Link to="/portfolio" className="btn btn-outline" style={{ padding: '5px 12px', fontSize: '12px', textDecoration: 'none' }}>
                            + Add
                          </Link>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
        </div>

        {/* News Sidebar */}
        <div className="card">
          <div className="card-title">Market News</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {news.map((item, i) => (
              <div key={i} style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', borderLeft: `3px solid ${item.sentiment === 'positive' ? '#00ff88' : item.sentiment === 'negative' ? '#ff4d4d' : '#ffb800'}` }}>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', lineHeight: '1.4', marginBottom: '6px' }}>
                  {item.headline}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{item.source}</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>{item.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
